import { lodestarColors, ursaColors } from "@smartsheet/design-tokens";
import { themed } from "../../../util/theme";
import { BaseButtonStyledProps } from "../types";
import { mapButtonAppearance } from "./mapButtonAppearance";

const buttonTextColorTheme = themed({
    primary: {
        enabled: {
            "lodestar-light": lodestarColors.neutralLight40,
            oldestar: "#ffffff",
            "ursa-light": lodestarColors.neutralLight40,
        },
        active: {
            "lodestar-light": lodestarColors.neutralLight40,
            oldestar: "#ffffff",
            "ursa-light": lodestarColors.neutralLight40,
        },
        disabled: {
            "lodestar-light": lodestarColors.neutralLight40,
            oldestar: "#ffffff",
            "ursa-light": lodestarColors.neutralLight40,
        },
    },
    secondary: {
        enabled: {
            "lodestar-light": lodestarColors.neutralDark30,
            oldestar: "#ffffff",
            "ursa-light": lodestarColors.neutralDark30,
        },
        active: {
            "lodestar-light": lodestarColors.neutralDark30,
            oldestar: "#ffffff",
            "ursa-light": lodestarColors.neutralDark30,
        },
        disabled: {
            "lodestar-light": lodestarColors.neutralAccessible,
            oldestar: "#ffffff",
            "ursa-light": lodestarColors.neutralAccessible,
        },
    },
    destructive: {
        enabled: {
            "lodestar-light": lodestarColors.neutralLight40,
            oldestar: "#ffffff",
            "ursa-light": lodestarColors.neutralLight40,
        },
        active: {
            "lodestar-light": lodestarColors.neutralLight40,
            oldestar: "#ffffff",
            "ursa-light": lodestarColors.neutralLight40,
        },
        disabled: {
            "lodestar-light": lodestarColors.neutralLight40,
            oldestar: "#ffffff",
            "ursa-light": lodestarColors.neutralLight40,
        },
    },
    "secondary-destructive": {
        enabled: lodestarColors.buttonDestructive,
        active: lodestarColors.buttonDestructive,
        disabled: lodestarColors.dangerLight20,
    },
    share: {
        enabled: {
            "lodestar-light": lodestarColors.neutralLight40,
            oldestar: "#ffffff",
            "ursa-light": lodestarColors.neutralLight40,
        },
        active: {
            "lodestar-light": lodestarColors.neutralLight40,
            oldestar: "#ffffff",
            "ursa-light": lodestarColors.neutralLight40,
        },
        disabled: {
            "lodestar-light": lodestarColors.neutralLight40,
            oldestar: "#ffffff",
            "ursa-light": lodestarColors.neutralLight40,
        },
    },
    borderless: {
        enabled: lodestarColors.ctaLink,
        active: lodestarColors.focusDark,
        disabled: lodestarColors.neutralDark10,
    },
    link: {
        enabled: {
            "lodestar-light": lodestarColors.ctaLink,
            "ursa-light": ursaColors.linkDefault,
        },
        active: {
            "lodestar-light": lodestarColors.focusDark,
            "ursa-light": ursaColors.linkDark,
        },
        disabled: lodestarColors.neutralDark10,
    },

    "primary-filled": {
        enabled: ursaColors.blue25,
        active: ursaColors.blue25,
        disabled: ursaColors.blue25,
    },
    "primary-empty": {
        enabled: ursaColors.blue600,
        active: ursaColors.blue600,
        disabled: ursaColors.blue300,
    },
    "secondary-filled": {
        enabled: ursaColors.neutral800,
        active: ursaColors.neutral800,
        disabled: ursaColors.neutral400,
    },
    "secondary-outlined": {
        enabled: ursaColors.neutral800,
        active: ursaColors.neutral800,
        disabled: ursaColors.neutral400,
    },
    "secondary-empty": {
        enabled: ursaColors.neutral800,
        active: ursaColors.neutral800,
        disabled: ursaColors.neutral400,
    },
    "canvas-filled": {
        enabled: ursaColors.slate700,
        active: ursaColors.slate700,
        disabled: ursaColors.slate400,
    },
    "canvas-empty": {
        enabled: ursaColors.slate700,
        active: ursaColors.slate700,
        disabled: ursaColors.slate400,
    },
    "destructive-filled": {
        enabled: ursaColors.neutral00,
        active: ursaColors.neutral00,
        disabled: ursaColors.neutral00,
    },
    "destructive-empty": {
        enabled: ursaColors.red600,
        active: ursaColors.red600,
        disabled: ursaColors.red400,
    },
});

// For mapping state to the fontSettings color object above
const mapStateToFontSettingsString = ({ isDisabled = false, isActive = false }) => {
    // Disabled should always win
    if (isDisabled) {
        return "disabled";
    }
    // Active should win over enabled
    if (isActive) {
        return "active";
    }
    return "enabled";
};

export const getButtonTextColor = ({
    appearance,
    state,
    themeMode,
}: Pick<BaseButtonStyledProps, "appearance" | "state" | "themeMode">): string => {
    const resolvedState = mapStateToFontSettingsString(state);
    return buttonTextColorTheme[mapButtonAppearance(appearance)][resolvedState](themeMode);
};
